import { useState } from "react";
import { submitEnquiry } from "../lib/api";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { ArrowRight, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const FIELD_LABELS = {
  name: "Full name",
  email: "Email address",
  town: "Town",
  postcode: "Postcode",
};

function FormField({ error, children }) {
  return (
    <div>
      {children}
      {error && (
        <p className="mt-1.5 text-xs text-red-600 flex items-start gap-1" role="alert" data-testid="enquiry-field-error">
          <AlertTriangle className="h-3 w-3 mt-0.5 flex-none" />
          <span>{error}</span>
        </p>
      )}
    </div>
  );
}

/**
 * Shared public enquiry form used on the Home, About and Contact pages.
 * Required fields: name, email, town, postcode. Phone + message are optional.
 * Field errors render inline (red text + red border) — never as a toast.
 * Toasts are reserved for success and transport-level failures only.
 *
 * Props:
 *   testIdPrefix     — used to scope data-testid="{prefix}-name" etc. so multiple
 *                      forms on a single page stay addressable from tests.
 *   compact          — slimmer paddings for narrow side-rail variant (About).
 *   showHeading      — render the "Make an enquiry" h3 inside the card.
 *   messagePlaceholder — override the textarea placeholder.
 */
export default function EnquiryForm({
  testIdPrefix = "enquiry",
  compact = false,
  showHeading = false,
  messagePlaceholder = "Tell us a bit about your driving experience...",
}) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", town: "", postcode: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const update = (key, value) => {
    setForm(f => ({ ...f, [key]: value }));
    if (fieldErrors[key]) setFieldErrors(e => { const n = { ...e }; delete n[key]; return n; });
  };

  const validate = () => {
    const errs = {};
    const trim = (v) => (v || "").trim();
    ["name", "email", "town", "postcode"].forEach(k => {
      if (!trim(form[k])) errs[k] = `${FIELD_LABELS[k]} is required.`;
    });
    if (!errs.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trim(form.email))) {
      errs.email = "Please enter a valid email address.";
    }
    if (!errs.postcode) {
      const pc = trim(form.postcode).toUpperCase();
      if (!/^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/.test(pc)) {
        errs.postcode = "Please enter a valid UK postcode (e.g. SO15 2BG).";
      }
    }
    return errs;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      const first = ["name", "email", "town", "postcode"].find(k => errs[k]);
      const el = document.querySelector(`[data-testid="${testIdPrefix}-${first}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setFieldErrors({});
    try {
      setSubmitting(true);
      await submitEnquiry(form);
      toast.success("Thanks — Akram will get back to you within one working day.");
      setForm({ name: "", email: "", phone: "", town: "", postcode: "", message: "" });
    } catch (err) {
      const raw = err?.response?.data?.detail;
      if (Array.isArray(raw) && raw.length > 0) {
        const newErrs = {};
        raw.forEach(d => {
          const k = Array.isArray(d.loc) ? d.loc[d.loc.length - 1] : "";
          if (["name", "email", "town", "postcode"].includes(k)) newErrs[k] = d.msg || "Invalid";
        });
        if (Object.keys(newErrs).length > 0) {
          setFieldErrors(newErrs);
        } else {
          toast.error("Could not send enquiry. Please call us instead.");
        }
      } else if (typeof raw === "string") {
        toast.error(raw);
      } else {
        toast.error("Could not send enquiry. Please call us instead.");
      }
    } finally { setSubmitting(false); }
  };

  const padding = compact ? "p-6 space-y-3" : "p-6 md:p-8 space-y-4";
  const inputCls = (k) =>
    `h-11 ${fieldErrors[k] ? "border-red-400 focus-visible:ring-red-200" : ""}`;

  return (
    <form onSubmit={onSubmit} className={`bg-white border border-black/5 rounded-2xl ${padding}`} data-testid={`${testIdPrefix}-form`} noValidate>
      {showHeading && (
        <h3 className="text-xl font-medium" style={{fontFamily:"Outfit, sans-serif"}}>Make an enquiry</h3>
      )}
      <FormField error={fieldErrors.name}>
        <Input data-testid={`${testIdPrefix}-name`} placeholder="Full name"
               value={form.name} onChange={e => update("name", e.target.value)}
               className={inputCls("name")} />
      </FormField>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
        <FormField error={fieldErrors.email}>
          <Input data-testid={`${testIdPrefix}-email`} type="email" placeholder="Email"
                 value={form.email} onChange={e => update("email", e.target.value)}
                 className={inputCls("email")} />
        </FormField>
        <Input data-testid={`${testIdPrefix}-phone`} placeholder="Phone (optional)"
               value={form.phone} onChange={e => update("phone", e.target.value)}
               className="h-11" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
        <FormField error={fieldErrors.town}>
          <Input data-testid={`${testIdPrefix}-town`} placeholder="Town"
                 value={form.town} onChange={e => update("town", e.target.value)}
                 className={inputCls("town")} />
        </FormField>
        <FormField error={fieldErrors.postcode}>
          <Input data-testid={`${testIdPrefix}-postcode`} placeholder="Postcode"
                 value={form.postcode} onChange={e => update("postcode", e.target.value.toUpperCase())}
                 className={inputCls("postcode")} />
        </FormField>
      </div>
      <Textarea data-testid={`${testIdPrefix}-message`} placeholder={messagePlaceholder}
                value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}
                rows={compact ? 4 : 5} />
      <Button type="submit" disabled={submitting}
              className="bg-[#37B160] hover:bg-[#2D9450] text-white h-11"
              data-testid={`${testIdPrefix}-submit`}>
        {submitting ? "Sending..." : "Send enquiry"} <ArrowRight className="h-4 w-4 ml-1" />
      </Button>
    </form>
  );
}
